"use client";

import { useState } from "react";

type FaqItem = { q: string; a: string };

/**
 * Landing-page FAQ list with a "Show N more" / "Show less" toggle.
 * Mirrors the pattern used on the pricing page (PricingFaq), but uses
 * the landing page's existing lp-faq-* visual treatment.
 */
export function LandingFaq({
  items,
  defaultVisibleCount = 5,
}: {
  items: FaqItem[];
  defaultVisibleCount?: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const [openQuestion, setOpenQuestion] = useState<string | null>(() => items[0]?.q ?? null);
  const hiddenCount = Math.max(0, items.length - defaultVisibleCount);
  const visibleItems = expanded ? items : items.slice(0, defaultVisibleCount);
  const defaultOpenQuestion = items[0]?.q ?? null;

  function toggleExpanded() {
    setExpanded((prev) => {
      const nextExpanded = !prev;
      if (!nextExpanded && !items.slice(0, defaultVisibleCount).some((item) => item.q === openQuestion)) {
        setOpenQuestion(defaultOpenQuestion);
      }

      return nextExpanded;
    });
  }

  return (
    <>
      <div className="lp-faq-list stagger">
        {visibleItems.map((item) => (
          <details key={item.q} open={openQuestion === item.q}>
            <summary
              onClick={(event) => {
                event.preventDefault();
                setOpenQuestion((current) => (current === item.q ? null : item.q));
              }}
            >
              {item.q}
            </summary>
            <p>{item.a}</p>
          </details>
        ))}
      </div>

      {hiddenCount > 0 ? (
        <button
          type="button"
          className="lp-faq-toggle"
          onClick={toggleExpanded}
          aria-expanded={expanded}
        >
          {expanded
            ? "Show less"
            : `Show ${hiddenCount} more question${hiddenCount === 1 ? "" : "s"}`}
        </button>
      ) : null}
    </>
  );
}
